/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from "react"
import type { Dispatch, SetStateAction } from "react"

import { QueryClient, type QueryObserverBaseResult, useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useLocalStorage, useUpdateEffect } from "react-use"

import { EUserQueryKey } from "@/3514/config"
import { type TUserQuery, useUserQuery } from "@/3514/queries"
import { EUserRole } from "@/3514/types"
import type {
    IUserAssistantDto,
    IUserAssistantModel,
    IUserCoachDto,
    IUserCoachModel,
    IUserDto,
    IUserManagerDto,
    IUserManagerModel,
    IUserParticipantDto,
    IUserParticipantModel
} from "@/3514/types"
import { isUnauthorizedError } from "@/3514/utils"
import { LocalStorageServiceKeys } from "@/services"
import type { TEmptyAsyncCallback, TEmptyCallback } from "@/shared/types/functions"

type TModelsMap = {
    [EUserRole.Participant]: IUserParticipantModel
    [EUserRole.Coach]: IUserCoachModel
    [EUserRole.Manager]: IUserManagerModel
    [EUserRole.Assistant]: IUserAssistantModel
}

type TDtosMap = {
    [EUserRole.Participant]: IUserParticipantDto
    [EUserRole.Coach]: IUserCoachDto
    [EUserRole.Manager]: IUserManagerDto
    [EUserRole.Assistant]: IUserAssistantDto
}

type TUserState<TRole extends EUserRole = EUserRole> = {
    user: TModelsMap[TRole] | undefined
    isLoading: boolean
    isLoaded: boolean
    set(userDto: TDtosMap[TRole]): Promise<void>
    reset: TEmptyAsyncCallback
    update(userDto: Partial<TDtosMap[TRole]>): Promise<void>
    refetch: Pick<QueryObserverBaseResult<TModelsMap[TRole], AxiosError>, "refetch">["refetch"]
}

function useUser<TRole extends EUserRole>(): TUserState<TRole> {
    const qc: QueryClient = useQueryClient()

    const [, , removeUserLoginKey]: [boolean, Dispatch<SetStateAction<boolean>>, TEmptyCallback] =
        useLocalStorage<boolean>(LocalStorageServiceKeys.MainUserLoggedOut())

    const setUser: (userDto: IUserDto) => Promise<void> = useCallback(
        async (userDto: IUserDto): Promise<void> => {
            await qc.setQueryData(EUserQueryKey(), userDto)
        },
        [qc]
    )

    const updateUser: (userDto: Partial<IUserDto>) => Promise<void> = useCallback(
        async (userDto: IUserDto): Promise<void> => {
            await qc.setQueryData(EUserQueryKey(), (prev: IUserDto): IUserDto => ({ ...prev, ...userDto }))
        },
        [qc]
    )

    const resetUser: TEmptyAsyncCallback = useCallback(async (): Promise<void> => {
        await qc.setQueryData(EUserQueryKey(), null)
    }, [])

    const {
        data: userData,
        error: userDataError,
        refetch: refetchUser,
        isFetched: isUserDataFetched,
        isSuccess: isUserDataSucceed,
        isLoading: isUserDataLoading
    }: TUserQuery = useUserQuery()

    useUpdateEffect((): void => {
        if (isUserDataSucceed) removeUserLoginKey()
    }, [isUserDataSucceed])

    useUpdateEffect((): void => {
        if (isUnauthorizedError(userDataError)) void setUser(null)
    }, [userDataError])

    return useMemo(
        (): TUserState<TRole> => ({
            user: userData as TModelsMap[TRole],
            isLoading: isUserDataLoading,
            isLoaded: isUserDataFetched,
            set: setUser,
            update: updateUser,
            reset: resetUser,
            refetch: refetchUser as Pick<QueryObserverBaseResult<TModelsMap[TRole], AxiosError>, "refetch">["refetch"]
        }),
        [userData, isUserDataFetched, isUserDataLoading, setUser, refetchUser]
    )
}

export { useUser }
export type { TUserState }
